// @flow
import type NextComponentType from 'next'
import Router from 'next/router'
import * as React from 'react'
import type { ComponentType } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import hasUserPermissions, { canDo } from './hasUserPermissions'
import getParamFromURL from '../getParamFromUrl'
// $FlowIgnore TS import
import Loading from '../components/Loading'
// $FlowIgnore typescript import
import { actionCreators } from '../me'
import * as roles from './roles'
import * as routes from '../routes'
import type { State } from '../store/reducer'
import type { Action, AsyncAction } from '../../types/Action'
import type { Page } from '../../types/Routes'
import type {
  CanDos,
  Role,
  GroupedRole,
  PagePermissionsArray,
  PagePermissionsWithActions
} from '../../types/Role'
import styles from './withAuthentication.css'

export const homePageRoutes = {
  [roles.ADMIN]: routes.chauffeursList(),
  [roles.DISPATCHER]: routes.planned(),
  [roles.DRIVER]: routes.planned(),
  [roles.PROVIDER]: routes.offers(),
  [roles.REVIEWER]: routes.finished(),
  [roles.LOGGED_IN]: routes.me()
}

function getHomeRouteFor (userRoles: Array<Role> = []) {
  const orderedRoles: Array<Role> = [
    roles.PROVIDER,
    roles.DRIVER,
    roles.DISPATCHER,
    roles.REVIEWER,
    roles.ADMIN
  ]
  const predominantRole: Role | GroupedRole =
    orderedRoles.find(role => userRoles.includes(role)) || roles.LOGGED_IN
  return homePageRoutes[predominantRole]
}

type WithAuthenticationProps = {
  isReady: boolean,
  hasFetchedRoles: boolean,
  isLoggedIn: boolean,
  loadMe: () => AsyncAction,
  userRoles: Array<Role>
}
type WithAuthenticationState = {
  canDo: CanDos,
  hasCheckedUserPermissions: boolean,
  pagePermissions: PagePermissionsArray,
  pagePermissionsWithActions: PagePermissionsWithActions,
  redirectTo: ?string
}

export const selectors = {
  isLoggedIn ({ login: { isLoggedIn = false } = {} }: State = {}): boolean {
    return isLoggedIn
  },
  isReady ({
    api: { didCheckForCookie = false } = {},
    me: { isFetching = false } = {}
  }: State = {}): boolean {
    return didCheckForCookie && !isFetching
  },
  hasFetchedRoles ({ me: { hasFetched = false } = {} }: State = {}): boolean {
    return hasFetched
  },
  userRoles ({ me: { roles = [] } = {} }: State = {}): Array<Role> {
    return roles
  }
}

export function createWithAuthentication (
  pagePermissions: PagePermissionsWithActions,
  ChildComponent: NextComponentType
) {
  const childComponentName =
    ChildComponent.displayName || String(ChildComponent.name || 'Component')
  return class WithAuthentication extends React.Component<
    WithAuthenticationProps,
    WithAuthenticationState
  > {
    static async getInitialProps (args: any) {
      if (typeof ChildComponent.getInitialProps === 'function') {
        return ChildComponent.getInitialProps(args)
      }
      return {}
    }

    get displayName () {
      return `WithAuthentication(${childComponentName})`
    }

    constructor (...props: any) {
      super(...props)
      this.state = {
        canDo: {
          access: false
        },
        hasCheckedUserPermissions: false,
        pagePermissions: pagePermissions.access,
        pagePermissionsWithActions: pagePermissions,
        redirectTo: getParamFromURL('redirect_to')
      }
    }

    redirectToLogin () {
      const { href, as } = routes.login({
        pathname: global.location.pathname,
        search: global.location.search
      })
      Router.push(href, as)
    }

    redirectOnLoginSuccess (userRoles: Array<Role>) {
      const { href, as } = getHomeRouteFor(userRoles)
      const { redirectTo } = this.state
      if (redirectTo) {
        const sanitizedRedirectTo = redirectTo
          .split('/')
          .filter(value => !!value)
          .join('/')
        Router.push(`/${sanitizedRedirectTo}`)
      } else {
        Router.push(href, as)
      }
    }

    redirectToNotAuthorized () {
      const { href, as } = routes.notAuthorized()
      Router.push(href, as)
    }

    redirectUser () {
      const { isLoggedIn, userRoles, isReady, hasFetchedRoles } = this.props
      const { pagePermissions } = this.state

      // false if:
      // 1) Login is required and there are no roles
      // 2) Logout is required and there is any role
      // 3) A specific role is required and the user doesn't have it
      if (isReady && !hasUserPermissions(userRoles, pagePermissions)) {
        if (!isLoggedIn || pagePermissions.includes(roles.LOGGED_IN)) {
          // Handles (1): redirects to login page
          // Also handles (3), when a user is logged out
          this.redirectToLogin()
        } else if (pagePermissions.includes(roles.LOGGED_OUT)) {
          // Handles (2): redirects to the home page
          this.redirectOnLoginSuccess(userRoles)
        } else {
          // Handles (3): redirects to not authorized
          this.redirectToNotAuthorized()
        }
      }
    }

    fetchRoles () {
      const { isLoggedIn, isReady, hasFetchedRoles, loadMe } = this.props
      if (isReady && isLoggedIn && !hasFetchedRoles) {
        loadMe()
      }
    }

    doesPageRequireRolesToCheck (pagePermissions: PagePermissionsArray) {
      return (
        pagePermissions.length > 0 &&
        !pagePermissions.includes(roles.LOGGED_OUT)
      )
    }

    checkUserPermissions () {
      const { isLoggedIn, isReady, hasFetchedRoles } = this.props
      if (!isReady) {
        return
      }
      if (isLoggedIn && !hasFetchedRoles) {
        return this.fetchRoles()
      }
      this.redirectUser()
      if (!this.state.hasCheckedUserPermissions) {
        this.setState({
          hasCheckedUserPermissions: true,
          canDo: canDo(
            this.props.userRoles,
            this.state.pagePermissionsWithActions
          )
        })
      }
    }

    componentDidMount () {
      this.checkUserPermissions()
    }
    componentDidUpdate (prevProps: WithAuthenticationProps) {
      if (
        this.props.isReady !== prevProps.isReady ||
        this.props.hasFetchedRoles !== prevProps.hasFetchedRoles ||
        this.props.isLoggedIn !== prevProps.isLoggedIn
      ) {
        this.checkUserPermissions()
      }
    }

    render () {
      if (
        this.doesPageRequireRolesToCheck(this.state.pagePermissions) &&
        !this.props.hasFetchedRoles
      ) {
        return <Loading />
      }
      if (!this.state.hasCheckedUserPermissions) {
        return <div className={styles.loadingMessage}>Authenticating...</div>
      }
      if (!this.state.canDo.access) {
        return null
      }
      return <ChildComponent {...this.props} canDo={this.state.canDo} />
    }
  }
}

export default (pagePermissions: PagePermissionsWithActions) => (
  ChildComponent: ComponentType<*>
) => {
  function mapStateToProps (state: State) {
    return {
      hasFetchedRoles: selectors.hasFetchedRoles(state),
      isLoggedIn: selectors.isLoggedIn(state),
      isReady: selectors.isReady(state),
      userRoles: selectors.userRoles(state)
    }
  }

  function mapDispatchToProps (dispatch: Dispatch) {
    return bindActionCreators(actionCreators, dispatch)
  }

  return connect(
    mapStateToProps,
    mapDispatchToProps
  )(createWithAuthentication(pagePermissions, ChildComponent))
}
